import { Address } from '@synergy/models';
import { Box, Tooltip } from '@mui/material';
import { Field, FieldProps, FormikProps, getIn } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components';
import {
    PaloField,
    PaloFormGrid,
    PaloOverflowMenu,
    PaloRecordCard,
    PaloSegmentedToggle,
} from '@synergy/components-palo';
import { TextField } from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { tokens } from '@synergy/theme-palo';
import dayjs from 'dayjs';

import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialAddress,
    PoolAccountAddressAutocompleteList,
} from '../../../common/components/autocomplete-list/account-address-autocomplete-list/pool-account-address-autocomplete-list';
import { SalesOrderDetailFormValues } from '../models/sales-order-detail-form-values';
import { SalesOrderDetailPositionTable } from '../components/sales-order-detail-position-table';
import { formatCalendarWeek } from '../../../common/utils/calendar-week';

interface PaloSalesOrderDetailPositionProps {
    index: number;
    readonly?: boolean;
    onCustomerRemove: (index: number) => void;
    focusAfterMount?: boolean;
}

interface PaloSalesOrderDetailPositionState {
    open: boolean;
}

/**
 * One delivery address of an order, as a card: where it goes and by when in
 * the head, who receives it in the line below, and its articles. In the code
 * it stays a SalesOrderPosition; on the screen it is the delivery address it
 * is (#2259, as the customers of an inquiry in #2252).
 */
export class PaloSalesOrderDetailPosition extends FormComponent<
    SalesOrderDetailFormValues,
    PaloSalesOrderDetailPositionProps,
    PaloSalesOrderDetailPositionState
> {
    public state: PaloSalesOrderDetailPositionState = { open: true };

    private getCurrentAccountId(formikBag: FormikProps<SalesOrderDetailFormValues>) {
        return formikBag.values.customerAccount?.id;
    }

    private handleDeliveryAddressSelected =
        (formikBag: FormikProps<SalesOrderDetailFormValues>, pathPrefix: string) => (address: Address | null) => {
            formikBag.setFieldValue(`${pathPrefix}deliveryAddress`, address);
            if (address && address.costCentre) {
                formikBag.setFieldValue(`${pathPrefix}costCentre`, address.costCentre);
            }
        };

    private renderDeliveryDate(prefix: string) {
        const { readonly } = this.props;

        return (
            <Field name={`${prefix}isDesiredDeliveryDateFrom`}>
                {({ field, form }: FieldProps<boolean>) => {
                    const date = getIn(form.values, `${prefix}desiredDeliveryDate`);
                    const week = formatCalendarWeek(date);
                    // A date gone by is a slip while the order is still being
                    // written; on one that is through it is history (#2251).
                    const inThePast = !readonly && !!date && dayjs(date).isBefore(dayjs(), 'day');

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Box component="span" sx={{ fontSize: '12px', color: tokens.color.ink3 }}>
                                <FormattedMessage id="app.label.deliveryDate" />
                            </Box>
                            <PaloSegmentedToggle
                                size="small"
                                value={field.value ?? false}
                                onChange={this.setFieldValue(form, field.name)}
                                disabled={readonly}
                                options={[
                                    {
                                        value: false,
                                        label: <FormattedMessage id="app.salesOrders.detail.position.until" />,
                                    },
                                    {
                                        value: true,
                                        label: <FormattedMessage id="app.salesOrders.detail.position.from" />,
                                    },
                                ]}
                            />
                            <Box sx={{ width: 150 }}>
                                <FormikPoolDatePicker
                                    name={`${prefix}desiredDeliveryDate`}
                                    readOnly={readonly}
                                    disablePast={true}
                                    quietPast={true}
                                />
                            </Box>
                            {week && (
                                <Box component="span" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    {week}
                                </Box>
                            )}
                            {inThePast && (
                                <Tooltip title={<FormattedMessage id="app.common.deliveryDate.inThePast" />}>
                                    <WarningAmberOutlined sx={{ fontSize: 16, color: tokens.color.warn }} />
                                </Tooltip>
                            )}
                        </Box>
                    );
                }}
            </Field>
        );
    }

    public render() {
        const { readonly, index, onCustomerRemove } = this.props;
        const prefix = `positions.${index}.`;

        return (
            <Field name={`${prefix}deliveryAddress`}>
                {({ field: addressField, form }: FieldProps<PartialAddress>) => {
                    const address = addressField.value as Address | undefined;
                    const faulty = form.submitCount > 0 && !!getIn(form.errors, `positions.${index}`);
                    const label = address
                        ? [
                              [address.street, address.number].filter(Boolean).join(' '),
                              [address.zip, address.city].filter(Boolean).join(' '),
                          ]
                              .filter(Boolean)
                              .join(', ')
                        : undefined;

                    return (
                        <PaloRecordCard
                            id={`sales-order-position-${index}`}
                            title={
                                <>
                                    <FormattedMessage
                                        id="app.salesOrders.detail.deliveryAddressNumber"
                                        values={{ value: index + 1 }}
                                    />
                                    {label ? ` · ${label}` : ''}
                                </>
                            }
                            headerControl={this.renderDeliveryDate(prefix)}
                            actions={
                                readonly ? undefined : (
                                    <PaloOverflowMenu
                                        actions={[
                                            {
                                                label: (
                                                    <FormattedMessage id="app.salesOrders.detail.removeDeliveryAddress" />
                                                ),
                                                danger: true,
                                                onClick: () => onCustomerRemove(index),
                                            },
                                        ]}
                                    />
                                )
                            }
                            // A required field missing after a save opens the card and marks it (#2326 R-245).
                            open={this.state.open || faulty}
                            onToggle={() => this.setState(state => ({ open: !(state.open || faulty) }))}
                            faulty={faulty}
                        >
                            <PaloFormGrid columns={4} labels="side">
                                <PaloField
                                    label={
                                        <>
                                            <FormattedMessage id="app.label.deliveryAddress" /> *
                                        </>
                                    }
                                    name={addressField.name}
                                    span={2}
                                    error={
                                        this.hasError(form, addressField.name)
                                            ? this.getError(form, addressField.name)
                                            : undefined
                                    }
                                >
                                    <PoolAccountAddressAutocompleteList
                                        accountId={this.getCurrentAccountId(form)}
                                        autocompleteType="DeliveryAddress"
                                        label={<></>}
                                        disabled={!form.values.customerAccount}
                                        onSelected={this.handleDeliveryAddressSelected(form, prefix)}
                                        error={this.hasError(form, addressField.name)}
                                        readOnly={readonly}
                                        required={false}
                                        {...addressField}
                                    />
                                </PaloField>
                                <Field name={`${prefix}customerContact`}>
                                    {({ field }: FieldProps<PartialAccountContact>) => (
                                        <PaloField
                                            label={
                                                <>
                                                    <FormattedMessage id="app.label.customerContact" /> *
                                                </>
                                            }
                                            name={field.name}
                                            error={
                                                this.hasError(form, field.name)
                                                    ? this.getError(form, field.name)
                                                    : undefined
                                            }
                                        >
                                            <PoolAccountContactAutocompleteList
                                                accountId={this.getCurrentAccountId(form)}
                                                label={<></>}
                                                required={false}
                                                disabled={!form.values.customerAccount}
                                                onSelected={this.setFieldValue(form, field.name)}
                                                error={this.hasError(form, field.name)}
                                                readOnly={readonly}
                                                {...field}
                                            />
                                        </PaloField>
                                    )}
                                </Field>
                                <Field name={`${prefix}avisoContact`}>
                                    {({ field }: FieldProps<PartialAccountContact>) => (
                                        <PaloField
                                            label={<FormattedMessage id="app.label.avisoContact" />}
                                            name={field.name}
                                        >
                                            <PoolAccountContactAutocompleteList
                                                accountId={this.getCurrentAccountId(form)}
                                                label={<></>}
                                                disabled={!form.values.customerAccount}
                                                onSelected={this.setFieldValue(form, field.name)}
                                                error={this.hasError(form, field.name)}
                                                readOnly={readonly}
                                                {...field}
                                            />
                                        </PaloField>
                                    )}
                                </Field>
                                <Field name={`${prefix}costCentre`}>
                                    {({ field }: FieldProps<string>) => (
                                        <PaloField
                                            label={<FormattedMessage id="app.label.costCentre" />}
                                            name={field.name}
                                        >
                                            <TextField
                                                {...field}
                                                value={field.value ?? ''}
                                                size="small"
                                                fullWidth
                                                disabled={readonly}
                                            />
                                        </PaloField>
                                    )}
                                </Field>
                            </PaloFormGrid>

                            <Box sx={{ mt: '10px' }}>
                                <SalesOrderDetailPositionTable
                                    positionIndex={index}
                                    focusAfterMount={this.props.focusAfterMount}
                                    readonly={readonly}
                                />
                            </Box>
                        </PaloRecordCard>
                    );
                }}
            </Field>
        );
    }
}
